import { useRef, useState } from 'react';
import { Button, Modal, Input, Form } from 'semantic-ui-react';
import AGTable from 'src/components/table/aggrid';
import membersForms from 'src/services/api/membersForms';
import Swal from 'sweetalert2';
import { formatCurrency } from 'src/components/helpers';
import { agGridDateComparatorFromRow } from 'src/components/table/helpers';
import { screenSizes } from 'src/assets/styles/Literals';
import PaymentInfoModal from 'src/components/common/PaymentInfoModal';

const columns = ({ loading, openEditModal, handleDelete }) => [
  {
    headerName: 'Fecha',
    field: 'createdAt',
    valueGetter: (val) =>
      val?.data?.reference_sale
        ? new Date(val?.data?.createdAt).toLocaleDateString('es-CO')
        : new Date(val?.data?.date).toLocaleDateString('es-CO'),
    comparator: agGridDateComparatorFromRow((row) =>
      row?.reference_sale ? row?.createdAt : row?.date
    ),
  },
  {
    headerName: 'Valor',
    field: 'value',
    valueGetter: (val) => formatCurrency(val?.data?.value),
  },
  { headerName: 'Método de pago', field: 'payment_method_name' },
  {
    headerName: 'Tipo',
    field: 'type',
    valueGetter: (val) => (val?.data?.reference_sale ? 'Electrónica' : 'Manual'),
  },
  {
    headerName: 'Acciones',
    field: 'actions',
    cellRenderer: (val) => {
      const donation = val?.data;
      if (donation?.reference_sale) return null;
      return (
        <div style={{ display: 'flex', gap: 0 }}>
          <button
            className="ui mini button primary"
            style={{ padding: '4px 10px', fontSize: 13 }}
            onClick={() => openEditModal(donation)}
            disabled={loading}
          >
            Editar
          </button>
          <button
            className="ui mini button red"
            style={{ padding: '4px 10px', fontSize: 13 }}
            onClick={() => handleDelete(donation)}
            disabled={loading}
          >
            Eliminar
          </button>
        </div>
      );
    },
  },
];

const initialManualDonation = {
  createdAt: '',
  description: '',
  payment_method_name: '',
  reference_sale: '',
  value: '',
};

const LegadoAllGuestDonations = (props) => {
  const { setOpen, inputs, refreshDonations } = props;
  const [loading, setLoading] = useState(false);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [paymentInfoModalOpen, setPaymentInfoModalOpen] = useState(false);
  const [manualDonation, setManualDonation] = useState(initialManualDonation);
  const [editingDonation, setEditingDonation] = useState(null);
  const gridRef = useRef(null);

  const handleManualInput = (e, { name, value }) => {
    setManualDonation((prev) => ({ ...prev, [name]: value }));
  };

  const openEditModal = (donation) => {
    setEditingDonation(donation);
    setManualDonation({
      ...donation,
      date: donation.date || donation.createdAt?.slice(0, 10) || '',
      value: donation.value,
      payment_method_name: donation.payment_method_name,
    });
    setManualModalOpen(true);
  };

  const handleManualSubmit = async (e) => {
    e?.preventDefault();
    if (
      !manualDonation.value ||
      !manualDonation.date ||
      !manualDonation.payment_method_name ||
      !inputs.id
    ) {
      Swal.fire('Error', 'Por favor completa los campos obligatorios', 'error');
      return;
    }
    setLoading(true);
    try {
      if (editingDonation && editingDonation.id) {
        await membersForms.updateManualDonation({
          ...manualDonation,
          id: editingDonation.id,
          guest_id: inputs.id,
        });
        Swal.fire('Éxito', 'Donación manual actualizada', 'success');
      } else {
        await membersForms.addManualDonation({
          ...manualDonation,
          guest_id: inputs.id,
        });
        Swal.fire('Éxito', 'Donación manual agregada', 'success');
      }
      setManualModalOpen(false);
      setEditingDonation(null);
      if (refreshDonations) refreshDonations();
    } catch (err) {
      Swal.fire('Error', 'No se pudo guardar la donación', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (donation) => {
    if (!donation.id) return;
    const confirm = await Swal.fire({
      title: '¿Eliminar donación?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (confirm.value) {
      setLoading(true);
      try {
        await membersForms.deleteManualDonation({
          id: donation.id,
          guest_id: inputs.guest_id || inputs.id || inputs.identification,
        });
        if (refreshDonations) refreshDonations();
      } catch (err) {
        Swal.fire('Error', 'No se pudo eliminar la donación', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const onGridReady = (params) => {
    if (gridRef.current) {
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
      if (window.innerWidth > screenSizes.tablet) {
        params.api.sizeColumnsToFit();
      }
    }
  };

  return (
    <Modal open onClose={() => setOpen(false)} size="large">
      <Modal.Header>Mis Donaciones</Modal.Header>
      <Modal.Content scrolling>
        <p>
          <strong>Atención:</strong> Las donaciones manuales son para que puedas calcular tus
          avances personales en relación con las donaciones que realizas a través de otros canales,
          como transferencias, QR, sobres en efectivo, datáfonos o PSE.
        </p>
        <div style={{ marginBottom: 16 }}>
          <Button
            primary
            onClick={() => {
              setManualDonation(initialManualDonation);
              setEditingDonation(null);
              setManualModalOpen(true);
            }}
          >
            Agregar donación manual
          </Button>
        </div>
        <div style={{ height: '60vh', width: '100%' }}>
          <AGTable
            rowData={inputs.allPayments}
            columnDefs={columns({ loading, openEditModal, handleDelete })}
            rowHeight={50}
            pagination
            onGridReady={onGridReady}
            tableRef={gridRef}
          />
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setOpen(false)}>Cerrar</Button>
      </Modal.Actions>

      {/* Manual Donation Modal */}
      <Modal open={manualModalOpen} onClose={() => setManualModalOpen(false)} size="tiny">
        <Modal.Header>
          {editingDonation ? 'Editar donación presencial' : 'Agregar donación presencial'}
        </Modal.Header>
        <Modal.Content>
          <p>
            <strong>Atención:</strong> Estos datos son informativos de tus avances totales,
            incluyendo los manuales. Recuerda que las donaciones que realices a través de otros
            canales deberán ser reportadas por los diferentes medios que tiene la iglesia. Solo
            podrás solicitar tu certificado anual para la declaración de renta por las donaciones
            efectivamente ingresadas y reportadas en debida forma.{' '}
            <PaymentInfoModal
              open={paymentInfoModalOpen}
              setPaymentInfoModalOpen={setPaymentInfoModalOpen}
            />
          </p>
          <Form onSubmit={handleManualSubmit}>
            <Form.Field required>
              <label>Fecha</label>
              <Input
                name="date"
                type="date"
                value={manualDonation.date}
                onChange={handleManualInput}
                placeholder="2025-01-01"
              />
            </Form.Field>
            <Form.Field required>
              <label>Método de pago</label>
              <Input
                name="payment_method_name"
                value={manualDonation.payment_method_name}
                onChange={handleManualInput}
                placeholder="VISA, Efectivo, etc."
              />
            </Form.Field>
            <Form.Field required>
              <label>Valor</label>
              <Input
                name="value"
                value={manualDonation.value}
                onChange={handleManualInput}
                placeholder="Valor donado"
                type="number"
              />
            </Form.Field>
            <Button
              style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0 }}
              type="submit"
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            disabled={loading}
            onClick={() => {
              setManualModalOpen(false);
              setEditingDonation(null);
            }}
          >
            Cancelar
          </Button>
          <Button primary disabled={loading} loading={loading} onClick={handleManualSubmit}>
            {editingDonation ? 'Actualizar' : 'Agregar'}
          </Button>
        </Modal.Actions>
      </Modal>
    </Modal>
  );
};

export default LegadoAllGuestDonations;
